import { Color, PDFFont, PDFPage, rgb } from 'pdf-lib';
import { toPdfPoint } from './utils';

export interface Margin {
	top: number,
	right: number,
	bottom: number,
	left: number,
}

export interface Size {
	width: number,
	height: number,
}

export interface HeadingStyle {
	fontSize: number,
	bold?: boolean,
	italic?: boolean,
	color?: Color,
	margin?: Margin,
	textAlign?: string,
}

export interface TOCItem {
	level: number,
	text: string,
	listNumber?: string,
	pageNumber: number,
	targetPage?: PDFPage,

	noMargin?: boolean,
}


export interface PdfContext {
	counters: any,

	font: FontVariant,
	fontSize: number,
	textHeight: number,
	baselineAdjust: number,
	bold?: boolean,
	italic?: boolean,

	tocItems: TOCItem[],

	headings: HeadingStyle[],

	page: PDFPage,
	pageWidth: number,
	pageHeight: number,
	landscape: boolean,

	lineHeight?: number,
	
	leftEdge: number, // can change as get within tables/cells, lists etc, otherwise just margin left of page
	rightEdge: number,
	pageContentBottom: number, // includes page bottom margin and footer
	pageContentTop: number,  // includes page top margin and header

	retainLeftEdgeOnNewPage: boolean,

	x: number,
	y: number,

	yStaticOffset: number,

	color: Color,
	lineColor: Color,
	lineThickness: number,

	margin: Margin,

	listNumberPrefix?: string,

	//autoVertAlignCenterWithinRegion?: boolean,
	regionSize?: {
		w: number,
		h: number,
	},

	withinHeaderFooter: boolean, // if currently rendering header/footer content

	header?: HeaderFooter,
	footer?: HeaderFooter,

	pageBorder?: {
		width: number,
	},

	// NOTE: for being able to render the footer without it automatically creating new pages because its outside of the content area
	// have a flag to disable that for any footer content rendering:
	disableAutoNewPage?: boolean,

	// to insert pages at current point
	// this is needed for e.g. finalizing TOC which is making changes back at a page which isn't the last page in the doc
	addNewPagesAtThisPoint?: boolean,

	pageNumber: number,

	// want to be able to nest the measuring - i.e. content within a table cell has to be measured for a row, 
	// but the overall table also needs to be measured for page fit
	measureStack: string[],
	
	lineSpaceCount?: number,
	spaceAdjust?: number,

	spaceWidth?: number,

	newPageRequired?: boolean,
}

export interface FontVariant {
	regular: PDFFont,
	bold: PDFFont,
	italic: PDFFont,
	boldItalic: PDFFont,
}



export interface PdfFormatDefaults {
	pageSize: Size,
	margin: Margin,
	cellPadding: Margin,
	headings: HeadingStyle[],

	fontName: string, // "helvetica"
	fontSize: number,
	color: Color,
	lineHeight: number, // NOTE: browser only seems to support to one decimal point
	textAlign: string,

	baselineAdjust: number,
	
	listIndent: number,
	listStyle: string,
}

export const PdfDefaults: PdfFormatDefaults = {
	pageSize: {
		width: toPdfPoint(210),
		height: toPdfPoint(297),
	},
	
	margin: {
		top: toPdfPoint(10),
		right: toPdfPoint(25),
		bottom: toPdfPoint(10),
		left: toPdfPoint(25),
	},

	cellPadding: {
		top: toPdfPoint(1), right: toPdfPoint(2), bottom: toPdfPoint(1), left: toPdfPoint(2),
	},

	fontName: "tenorite",
	fontSize: 10,
	color: rgb(0,0,0),
	lineHeight: 1,
	textAlign: "left",

	baselineAdjust: 0.35,

	listIndent: 35,
	listStyle: "*.1.",

	headings: [
		{
			fontSize: 12,
		},
		{
			fontSize: 18,
			bold: true,
			margin: {
				top: 10, bottom: 5, left: 0, right: 0, 
			},
			//color: rgb(1,0,0),
		},
		{
			fontSize: 16,
			bold: true,
		},
		{
			fontSize: 14,
			bold: true,
		},
		{
			fontSize: 12,
			bold: true,
		},
	]
}

export interface Content {
	type?: string, 
	id?: number,

	visibility?: string, // conditional visibility script
	scriptEvents?: string,

	className?: string,
}

export const HorizAlign = {
	left: "left",
	right: "right",
	center: "center",
	justified: "justified",
}
export const VertAlign = {
	top: "top",
	bottom: "bottom",
	center: "center",
}

export interface Segment {
	text?: string,
	bold?: boolean,
	italic?: boolean,

	words?: string[],

	// object containing any def/options to define the field
	field?: any,

	wasCalculated?: boolean,

	newLine?: boolean,
}

export interface FieldOptions {
	prop?: string,

	script?: string,

	format?: string,
	forceCase?: string,

	bold?: boolean,
}


export interface Field extends Segment {
	id: number,
	field: FieldOptions,
}

export type AllSegments = Segment | Field;

export interface Paragraph extends Content {
	segments: AllSegments[],

	continue?: boolean, // continue prev para

	html?: string, // used for the designer to temporarily hold html before it gets converted back to segments

	align?: string,
	fontSize?: number,

	// way to specify for entire para
	bold?: boolean,
	italic?: boolean,

	forceCase?: string,

	overflow?: boolean, // auto overflow to next para element

	// other things like margins etc
}
export interface PageBreak extends Content {
}
export interface HorizRule extends Content {
}
export interface Heading extends Paragraph {
	level: number,
}
export interface Img extends Content {
	width: number,
	height: number,

	scale?: number,

	align?: string,
	margin?: Margin,

	image: {
		blob: string,
		title: string,
		ext: string,
	}
}

export interface Link extends Content {
	url: string,
	display?: "button",
	title: string,
}

export interface TOC extends Content {
	endItems?: string, // script
}

export interface Tag extends Content {
	content: AllContent[],

}

export interface TOCDocumentPosition {
	page: PDFPage,
	toc: TOC,
	context: PdfContext, // context at the point TOC was referenced
}

export interface ListItem extends Content {
	content: AllContent[],

	list?: string, // prop name of list to iterate over
	scopeName?: string,
	
	filter?: any, // ability to filter the items to only include ones that match 

	pageBreakBefore?: boolean,
}

export interface IterationProps {
	// iteration props ----------
	list?: string, // prop name of list to iterate over
	scopeName?: string, // e.g. if iterating of parties, scope might be "party"
	filter?: any, // ability to filter the items to only include ones that match 
	lookupContainer?: string; // if the list is an array of container ids then lookup the value from this container by id and use that
	// i.e. this is used for iterating over holders[] and getting ref to the party 

	counterName?: string, // name of a counter that increments with each iteration of this group

	newPage?: boolean,
	newPageSkipFirst?: boolean,

	repeatToPageBottom?: boolean,

	pageBreakBefore?: boolean,
	continuous?: boolean,
	//-----------	
}

export interface Group extends Content, IterationProps {
	content: AllContent[],

	margin?: Margin,

	keepTogether?: boolean,
}

export interface Region extends Content {
	content: AllContent[],
	position: {
		x: number,
		y: number,
	},
	size: {
		w: number,
		h: number,
	}
}

export interface List extends Content {
	style?: string; //"o" | "1." | "a." | "A." | "i." | "I.", "*.1.", "1)" etc
	
	margin?: Margin,
	
	indent?: number,

	itemMargin?: Margin,

	ignoreFormatOneItem?: boolean,

	start?: number, // i.e. count to start list items at

	keepItemsTogether?: boolean,

	items: ListItem[],

}

export type AllContent = 
	Paragraph
	| Table
	| PageBreak
	| Heading
	| List
;

export interface TableCell extends Content {
	content: AllContent[],

	cellPadding?: Margin,

	bgColor?: string,
	color?: string,
}

export interface TableRow extends Content, IterationProps {
	cells: TableCell[],

	// list?: string, // prop name of list to iterate over
	// scopeName?: string,
	// filter?: any, // ability to filter the items to only include ones that match 
}

export interface Table extends Content {
	cols: {
		id?: number,
		widthPercent: number,
	}[],

	cellPadding?: Margin,
	margin?: Margin,

	hideBorders?: boolean,

	allowBreak?: boolean,

	width?: string,

	rows: TableRow[],
}

export interface Section extends Content, IterationProps {
	continuous?: boolean,
	pageBreakBefore?: boolean,
	landscape?: boolean,
	fixedHeight?: number,

	content: AllContent[],

	border?: {
		width: number,
	}

	header?: HeaderFooter,
	footer?: HeaderFooter,

	// for static layout
	width?: number,
	height?: number,
	pdfPageIndex?: number,
}

export interface SubReport extends Content {
	report: {
		def: string, // report_def id
		title: string,
	}
}

export interface SignatureBlock extends Content {
	entity: string,
	content: AllContent[],
	width?: number,
	height?: number,

	sigHeight?: number,

	layout?: string, // compact
	
	retainFormatting?: boolean,
}

export interface HeaderFooter extends Content {
	height: number,
	hideFirstPage?: boolean,
	
	content: AllContent[],
}


export interface ReportStructure {
	idCounter: number,

	layoutType: "dynamic" | "static",

	sections: Section[],

	defaults: PdfFormatDefaults,

	template?: {
		blob: string,
		title: string,
	},

}

export interface report_def {
	id: string,
	title: string,
    data: ReportStructure,
	deleted: number,
	version: number,
	created: string,
	updated: string,
	meta: any,

	subReports: {
		[k: string]: report_def,
	}
}